import cn from "../../lib/classnames";
import React from "react";
import styles from "./TitledContentBlocks.module.css";
import {Column, Container, Row} from "../grid";

export type ContentBlock = {
    renderImage: (imageStyle?: string) => React.ReactNode;
    title?: string;
    content: string;
};

type Props = {
    header: string;
    blocks: Array<ContentBlock>;
    containerPadding?: string;
    blockTitleFontClassName?: string;
    blockContentFontClassName?: string;
};

export default function TitledContentBlocks(props: Props) {
    const titleStyles = cn(
        styles.title,
        props.blockTitleFontClassName ? props.blockTitleFontClassName : styles.defaultTitleFont,
    );
    const contentStyles = cn(
        styles.content,
        props.blockContentFontClassName
            ? props.blockContentFontClassName
            : styles.defaultContentFont,
    );
    function renderRemainder(noRemainder: boolean, blocks: ContentBlock[]) {
        return !noRemainder && blocks.length % 3 === 1 ? (
            <Row>
                <Column large={12} medium={8}>
                    <div className={styles.block}>
                        {blocks[blocks.length - 1].renderImage(styles.image)}
                        {blocks[blocks.length - 1].title ? (
                            <div className={titleStyles}>{blocks[blocks.length - 1].title}</div>
                        ) : null}
                        <div className={contentStyles}>{blocks[blocks.length - 1].content}</div>
                    </div>
                </Column>
            </Row>
        ) : null;
    }

    function getBlockRows(blocks: ContentBlock[]) {
        const noRemainder = blocks.length % 3 === 0;
        let blocksToRender = blocks.slice(0, blocks.length);

        if (!noRemainder && blocksToRender.length > 3) {
            if (blocks.length % 3 === 2) {
                blocksToRender = blocks.slice(0, blocks.length - 2);
            } else {
                blocksToRender = blocks.slice(0, blocks.length - 1);
            }
        }

        const blockRows: ContentBlock[][] = [];
        if (blocksToRender.length >= 3) {
            for (let i = 0; i < blocksToRender.length; i += 3) {
                blockRows.push([blocksToRender[i], blocksToRender[i + 1], blocksToRender[i + 2]]);
            }
        }

        let keyCounts = 0;

        return (
            <>
                {blockRows.map((tripletBlocks) => {
                    keyCounts += 4;
                    return (
                        <Row key={keyCounts}>
                            <Column key={keyCounts - 1} large={4} mediumOffset={1} medium={2}>
                                <div className={styles.block}>
                                    {tripletBlocks[0].renderImage(styles.image)}
                                    {tripletBlocks[0].title ? (
                                        <div className={titleStyles}>{tripletBlocks[0].title}</div>
                                    ) : null}
                                    <div className={contentStyles}>{tripletBlocks[0].content}</div>
                                </div>
                            </Column>
                            <Column key={keyCounts - 2} large={4} medium={2}>
                                <div className={styles.block}>
                                    {tripletBlocks[1].renderImage(styles.image)}
                                    {tripletBlocks[1].title ? (
                                        <div className={titleStyles}>{tripletBlocks[1].title}</div>
                                    ) : null}
                                    <div className={contentStyles}>{tripletBlocks[1].content}</div>
                                </div>
                            </Column>
                            <Column key={keyCounts - 3} large={4} medium={2}>
                                <div className={styles.block}>
                                    {tripletBlocks[2].renderImage(styles.image)}
                                    {tripletBlocks[2].title ? (
                                        <div className={titleStyles}>{tripletBlocks[2].title}</div>
                                    ) : null}
                                    <div className={contentStyles}>{tripletBlocks[2].content}</div>
                                </div>
                            </Column>
                        </Row>
                    );
                })}
                {!noRemainder && blocks.length % 3 === 2 ? (
                    <Row key={keyCounts + 1}>
                        <Column key={keyCounts + 2} large={6} mediumOffset={1} medium={3}>
                            <div className={styles.block}>
                                {blocks[blocks.length - 2].renderImage(styles.image)}
                                {blocks[blocks.length - 2].title ? (
                                    <div className={titleStyles}>
                                        {blocks[blocks.length - 2].title}
                                    </div>
                                ) : null}
                                <div className={contentStyles}>
                                    {blocks[blocks.length - 2].content}
                                </div>
                            </div>
                        </Column>
                        <Column key={keyCounts + 3} large={6} medium={3}>
                            <div className={styles.block}>
                                {blocks[blocks.length - 1].renderImage(styles.image)}
                                {blocks[blocks.length - 1].title ? (
                                    <div className={titleStyles}>
                                        {blocks[blocks.length - 1].title}
                                    </div>
                                ) : null}
                                <div className={contentStyles}>
                                    {blocks[blocks.length - 1].content}
                                </div>
                            </div>
                        </Column>
                    </Row>
                ) : (
                    renderRemainder(noRemainder, blocks)
                )}
            </>
        );
    }

    return (
        <div
            className={cn(
                styles.container,
                props.containerPadding ? props.containerPadding : styles.defaultContainerPadding,
            )}
        >
            <Container>
                <Row>
                    <Column large={10} largeOffset={1} medium={8}>
                        <div className={styles.header}>{props.header}</div>
                    </Column>
                </Row>
                {getBlockRows(props.blocks)}
            </Container>
        </div>
    );
}
